import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserEditService } from '../services/userEdit.service';
import type { UserEditFormData } from '../types/userEdit.types';
import { toast } from 'sonner';

export function useUserEdit(userId?: string) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UserEditFormData>({
    full_name: '',
    email: '',
    phone: '',
    birth_year: '',
    gender: 'male',
    status: 'active',
    role_code: '',
    job_title: '',
    skills: [],
    education_level: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: ''
  });

  const { data: user, isLoading } = useQuery({
    queryKey: ['user-edit', userId],
    queryFn: () => UserEditService.getUserById(userId!),
    enabled: !!userId,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        birth_year: user.birth_year || '',
        gender: user.gender || 'male',
        id_number: user.id_number || '',
        address: user.address || '',
        role_code: user.role_code || '',
        job_title: user.job_title || '',
        skills: user.skills || [],
        education_level: user.education_level || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        emergency_contact_relation: user.emergency_contact_relation || '',
        status: user.status || 'active'
      });
    }
  }, [user]);

  const updateField = (field: keyof UserEditFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<UserEditFormData>) => UserEditService.updateUser(userId!, payload),
    onSuccess: () => {
      toast.success('Cập nhật thông tin nhân sự thành công');
      queryClient.invalidateQueries({ queryKey: ['user-edit', userId] });
      queryClient.invalidateQueries({ queryKey: ['users-list'] });
    },
    onError: (error: any) => {
      toast.error('Lỗi khi cập nhật nhân sự: ' + error.message);
    }
  });

  const handleSave = () => {
    if (!userId) return;
    updateMutation.mutate(formData);
  };

  return {
    formData,
    isLoading,
    isSaving: updateMutation.isPending,
    updateField,
    handleSave
  };
}
